<template>
  <div v-if="permPlacement.reference_number" class="details-wrapper">
    <div class="details-header">
      <object-header :header-data="objectHeader" />
      <VueHtml2pdf
        ref="DownloadComp"
        :show-layout="false"
        :paginate-elements-by-height="1400"
        pdf-format="a4"
        :manual-pagination="false"
        :enable-download="true"
        :filename="`PermPlacement-${permPlacement.reference_number}`"
      >
        <section slot="pdf-content">
          <PermPlacementDetailsPdf />
        </section>
      </VueHtml2pdf>
      <TolButton
        :tooltip="$t('permPlacement.labels.saveAsPdf')"
        button-type="minor"
        icon-name="file-pdf"
        class="action-round"
        @click="exportAsPdf()"
      />
    </div>
    <div v-if="permPlacement.reference_number" class="details-body">
      <div class="content-panel-wrapper">
        <div v-scroll-spy data-scroll-spy-id="main-tab" class="content-panel">
          <section class="details-section">
            <PermPlacementDetailsForm :perm-placement="permPlacement" />
          </section>
          <section class="details-section">
            <h1>{{ $t('permPlacement.labels.attachments') }}</h1>
            <div v-if="canViewAttachment && permPlacement && permPlacement.reference_number">
              <Attachment
                :reference-number="permPlacement.reference_number"
                owner="PERM_PLACEMENT"
                @attachment-success="refreshAttachments"
              />
              <AttachmentsList
                attachment-type="PERM_PLACEMENT"
                :attachments="permPlacementAttachments"
                :attachment-options="attachmentOptions"
                @delete-attachment="confirmAttachmentDelete"
                @refresh="refreshAttachments"
              />
            </div>
          </section>
          <section v-if="permPlacementHistories.length" class="details-section">
            <h1>{{ $t('permPlacement.labels.history') }}</h1>
            <tol-table
              :table-data="permPlacementHistories"
              :columns="historiesColumns"
              :expandable="true"
            >
              <template v-slot:tableHeader>
                <th></th>
                <th>{{ $t('permPlacement.history.listing.labels.created_at') }}</th>
                <th>{{ $t('permPlacement.history.listing.labels.created_by_name') }}</th>
                <th>{{ $t('permPlacement.history.listing.labels.status') }}</th>
                <th>{{ $t('permPlacement.history.listing.labels.start_date') }}</th>
                <th>{{ $t('permPlacement.history.listing.labels.createdComment') }}</th>
              </template>
              <template v-slot:created_at="{ value }">
                {{ formatDate(value.created_at) }}
              </template>
              <template v-slot:status="{ value }">
                <Status :status="value.status" object-type="assignment" />
              </template>
              <template v-slot:expanded="{ value }">
                <PermPlacementDetailsForm
                  :perm-placement="getSelectedRecordHistory(value.id)"
                  :is-history-record="true"
                />
              </template>
            </tol-table>
          </section>
        </div>
      </div>
      <div class="perm-status" :class="statusClass">
        {{ permPlacement.status }}
      </div>
    </div>
    <div v-else class="empty">
      {{ $t('permPlacement.details.errors.noPerm') }}
    </div>
    <ConfirmModal
      v-if="isDeleteAttachmentModalDisplayed"
      action="DELETE_ATTACHMENT"
      :title="$t('attachments.confirmDeleteTitle')"
      @continue-action="deleteAttachment()"
      @cancel-action="cancelDeletingAttachment()"
    >
      <template v-slot:modalMessage>
        {{ $t('attachments.confirmDelete') }}
      </template>
    </ConfirmModal>
  </div>
</template>

<script>
import { AppActions } from '@/app/app.keys';
import { attachmentService } from '@/app/components/attachments/attachment.service';
import ConfirmModal from '@/app/components/ConfirmModal';
import ObjectHeader from '@/app/components/ObjectHeader';
import {
  PermPlacementDetailsGetters,
  PermPlacementDetailsActions
} from './perm-placement-details.keys';
import Status from '@/app/components/Status';
import TolTable from '@/app/components/TolTable';
import TolButton from '@/app/components/TolButton';
import PermPlacementDetailsForm from './components/PermPlacementDetailsForm';
import Attachment from '@/app/components/attachments/Attachment';
import AttachmentsList from '@/app/components/attachments/AttachmentsList';
import format from 'date-fns/format';
import formatDatetime from '@/filters/format-datetime.filter.js';
import PermPlacementDetailsPdf from './components/PermPlacementDetailsPdf';

export default {
  name: 'PermPlacementDetails',
  components: {
    ConfirmModal,
    Status,
    PermPlacementDetailsForm,
    TolTable,
    TolButton,
    Attachment,
    AttachmentsList,
    ObjectHeader,
    PermPlacementDetailsPdf
  },
  data() {
    return {
      sidebarMenu: [
        {
          label: this.$t('permPlacement.labels.details'),
          submenu: [
            { label: this.$t('permPlacement.labels.candidatePlaced') },
            { label: this.$t('permPlacement.labels.clientDetails') },
            { label: this.$t('permPlacement.labels.outsourceAndSplit') },
            { label: this.$t('permPlacement.labels.guarantee') },
            { label: this.$t('permPlacement.labels.stakeholdersDetails') },
            { label: this.$t('permPlacement.labels.miscDetails') }
          ]
        },
        { label: this.$t('permPlacement.labels.attachments') },
        { label: this.$t('permPlacement.labels.history') }
      ],
      historiesColumns: ['created_at', 'created_by_name', 'status', 'start_date', 'createdComment'],
      attachmentOptions: ['Perm Placement', 'Other'],
      isDeleteAttachmentModalDisplayed: false,
      selectedAttachmentId: null
    };
  },
  computed: {
    currentUser() {
      return this.$store.state.currentUser;
    },
    statusClass() {
      let className = '';
      switch (this.permPlacement.status) {
        case 'DRAFT':
          className = 'draft';
          break;
        case 'SUBMITTED':
          className = 'submitted';
          break;
        case 'PROCESSED':
          className = 'processed';
          break;
        case 'CANCELLED':
          className = 'cancelled';
          break;
        case 'REJECTED':
          className = 'rejected';
          break;
        case 'OBSOLETE':
          className = 'obsolete';
          break;
        case 'FINISHED':
          className = 'finished';
          break;
      }
      return className;
    },
    permPlacement() {
      return this.$store.getters[PermPlacementDetailsGetters.CurrentPermPlacement];
    },
    permPlacementHistories() {
      return this.$store.getters[PermPlacementDetailsGetters.CurrentPermPlacementHistories];
    },
    permPlacementAttachments() {
      return this.$store.getters[PermPlacementDetailsGetters.CurrentPermPlacementAttachments];
    },
    canViewAttachment() {
      return this.currentUser.permissions['PERM_PLACEMENT']['VIEW_ATTACHMENT'];
    },
    objectHeader() {
      return {
        referenceNumber: this.permPlacement.reference_number,
        objectStatus: this.permPlacement.status,
        objectType: 'PERM_PLACEMENT',
        lastModified: `${this.permPlacement.lastModified.first_name} ${
          this.permPlacement.lastModified.last_name
        } at ${formatDatetime(this.permPlacement.created_at)}`
      };
    }
  },
  watch: {
    $route: {
      immediate: true,
      handler(val) {
        this.fetchPermPlacement(val.params.referenceNumber);
      }
    }
  },
  mounted() {
    this.$store.dispatch(AppActions.SetPageTitle, this.$t('permPlacement.details.title'));
    this.fetchPermPlacement(this.$route.params.referenceNumber);
  },
  methods: {
    exportAsPdf() {
      this.$refs.DownloadComp.generatePdf();
    },
    fetchPermPlacement(referenceNumber) {
      this.$store.dispatch(PermPlacementDetailsActions.RetrievePermPlacement, referenceNumber);
    },
    getSelectedRecordHistory(id) {
      return this.$store.getters[PermPlacementDetailsGetters.SelectedRecordHistory](id);
    },
    formatDate(d) {
      return d ? format(d, 'YYYY-MM-DD') : null;
    },
    confirmAttachmentDelete(attachmentId) {
      this.selectedAttachmentId = attachmentId;
      this.isDeleteAttachmentModalDisplayed = true;
    },
    async deleteAttachment() {
      await attachmentService.deleteAttachment(this.selectedAttachmentId);
      this.isDeleteAttachmentModalDisplayed = false;
      this.selectedAttachmentId = null;
      this.refreshAttachments();
    },
    cancelDeletingAttachment() {
      this.isDeleteAttachmentModalDisplayed = false;
      this.selectedAttachmentId = null;
    },
    refreshAttachments() {
      this.$store.dispatch(
        PermPlacementDetailsActions.RetrievePermPlacementAttachments,
        this.$route.params.referenceNumber
      );
    }
  }
};
</script>

<style scoped lang="scss">
@import '@/app/styles/colors.scss';
@import '@/app/styles/variables.scss';

.details-wrapper {
  display: flex;
  flex-direction: column;

  background-color: $background-level-2;
  .details-header {
    display: flex;
    align-items: center;
    padding: 0 $details-padding;
    border-bottom: 2px solid $row-border;
    justify-content: space-between;
    .object-header {
      border-bottom: 0;
    }
  }

  .details-body {
    position: relative;
    border-bottom: 1px solid $row-border;
    display: flex;
    flex-direction: row;
    justify-content: center;
  }

  .content-panel-wrapper {
    flex: 0 85%;
    .content-panel {
      display: block;
      background-color: $white;
      padding: 60px;
    }
  }

  .details-section {
    padding-bottom: 25px;

    h1 {
      border-bottom: 1px solid $row-border;
    }
  }

  .perm-status {
    position: fixed;
    width: 170px;
    padding: 10px 0;
    transform: rotate(-90deg);
    top: 50%;
    right: 0;
    color: $white;
    font-weight: bold;
    text-align: center;
    &.draft {
      background-color: $light-grey-1;
      border: 1px solid $dark-grey-1;
    }
    &.submitted {
      background-color: $orange;
      border: 1px solid $secondary-orange;
    }
    &.active-online {
      background-color: $secondary-green;
      border: 1px solid $green;
    }
    &.finished,
    &.processed {
      background-color: $blue;
      border: 1px solid $dark-blue;
    }
    &.rejected {
      background-color: $red;
      border: 1px solid $secondary-red;
    }
    &.obsolete {
      background-color: $red;
      border: 1px solid $secondary-red;
    }
    &.active-offline {
      background-color: $purple;
      border: 1px solid $purple;
    }
  }
}
</style>
